import { z } from "zod";

// TES-1074 point 5 — "Aktuellt": what is happening now and what the coach
// needs to do. A-01:
// "Aktuellt ska tydligt visa vad som förändrats, vad coachen behöver göra nu, senaste och nästa incheckning, öppna uppföljningar eller överenskommelser, senaste kontakt samt neutral medicinsk status när den påverkar coachningen."
//
// A read model, derived on every request from the tables that already hold
// the facts (form submissions, the coach's check-in handling, the coach
// chat, follow-ups, Patientminnet, medical review cases). Nothing is stored,
// so it cannot drift from the surfaces that act on the same rows.
//
// Point 6: counts, dates and the coach's own follow-up and agreement text.
// No check-in answer, no message text, no medical case content: a medical
// review case is reduced to one fixed neutral sentence.

// A check-in routed to the doctor shows MEDICAL_REVIEW_NEUTRAL_STATUS
// (medical-review-case.ts), A-01's sentence — the one constant every coach
// view uses (TES-1213). This is the same sentence without the check-in
// clause, for a case the coach herself sent from the chat or a shared
// reflection ("Skicka till läkare"): A-01's sentence names "incheckningen",
// which that case has none of.
export const COACH_MEDICAL_STATUS_OTHER =
  "Medicinsk uppföljning pågår. Du behöver inte bedöma eller kommentera detta.";

export const coachCheckinStateSchema = z.enum([
  "open",
  "answered",
  "closed_without_reply",
  // Routed to the doctor: the coach has nothing to do with it.
  "medical",
]);
export type CoachCheckinState = z.infer<typeof coachCheckinStateSchema>;

export const coachNowSchema = z.object({
  patientId: z.string(),
  coachAccess: z.enum(["active", "inactive"]),
  // TES-1121 (Ö-342 a) — true when coach access is inactive because she has
  // never had a subscription; the coach chat opens when one starts.
  // Optional so a server that predates it reads as the plan rule.
  coachOpensWithSubscription: z.boolean().optional(),
  // What waits for her, as counts.
  toDo: z.object({
    openCheckins: z.number().int().nonnegative(),
    unreadMessages: z.number().int().nonnegative(),
    dueFollowUps: z.number().int().nonnegative(),
  }),
  // What changed since the coach last wrote to the patient. `since` is null
  // when there has been no coach message at all; the counts then cover
  // everything on file.
  changes: z.object({
    since: z.string().nullable(),
    checkins: z.number().int().nonnegative(),
    messages: z.number().int().nonnegative(),
    memoryRows: z.number().int().nonnegative(),
  }),
  latestCheckin: z
    .object({
      submissionId: z.string(),
      submittedAt: z.string(),
      state: coachCheckinStateSchema,
    })
    .nullable(),
  // The weekly window: TES-1159, hers, anchored to her first dose (09:00
  // tenant time on the dose date + 7 days, then every 7 days); the Friday
  // 09:00 clock only for weight stabilisation without a first dose.
  // `openNow` when the current window is open and nothing has been submitted
  // in it yet; before her first window, its opening with `openNow: false`.
  //
  // TES-1118 — null when she has no trialing or active subscription. The
  // weekly check-in is part of the subscription (Ö-62, TES-543; TES-1021):
  // the patient's Home card, the app section and the reminder push are all
  // gated on it, so the coach must not be told a window is open for her.
  nextCheckin: z
    .object({
      opensAt: z.string(),
      openNow: z.boolean(),
    })
    .nullable(),
  followUps: z.array(
    z.object({
      id: z.string(),
      dueAt: z.string(),
      prompt: z.string(),
      status: z.enum(["scheduled", "due"]),
    }),
  ),
  agreements: z.array(
    z.object({
      itemId: z.string(),
      body: z.string(),
      observedAt: z.string(),
    }),
  ),
  latestContact: z.object({
    coachAt: z.string().nullable(),
    patientAt: z.string().nullable(),
  }),
  medicalStatus: z.string().nullable(),
});
export type CoachNow = z.infer<typeof coachNowSchema>;
